import { DateFormat, formatDateTimeWithLocale } from '@frontend/common';
import { DefaultPrimaryButton, DefaultSecondaryButton, Spinner } from '@frontend/components/common';
import { ICONS } from '@frontend/icons';
import { CommonMessage, useLocale } from '@frontend/lang';
import { TransactionStateBadge } from '@frontend/transaction/common';
import { TransactionStateName, TransactionTriggerName } from '@frontend/transaction/types';
import { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { DetailCard } from '../../components/detail-card.component';
import { EntityCard } from '../../components/entity-card.component';
import { KioskNavigationFooter } from '../../components/kiosk-navigation-footer.component';
import { KioskNavigationHeader } from '../../components/kiosk-navigation-header.component';
import { useAppSelector } from '../../redux/store';
import { useKioskNavigate } from '../../utils/kiosk-navigation';
import { openSlot } from '../../utils/slot-opener';
import { UpdateTransactionStatusModal } from '../slot-detail/update-transaction-status-modal.component';
import usePudoTransactionDetail from './pudo-transactions-detail.controller';

const formatTrackingNumber = (id: string): string => {
    if (id.length <= 12) return id;
    return `${id.substring(0, 8)}...${id.substring(id.length - 4)}`;
};

const formatDate = (date: Date, locale?: string) => formatDateTimeWithLocale(date, locale, DateFormat.DD_MM_YYYY_HH_MM);

const PudoTransactionDetail = () => {
    const navigate = useKioskNavigate();
    const viewProps = usePudoTransactionDetail();
    const navigationState = useAppSelector((store) => store.navigation);
    const [isOpeningSlot, setIsOpeningSlot] = useState(false);
    const [updateStatusModalOpen, setUpdateStatusModalOpen] = useState(false);
    const [pendingStatusTrigger, setPendingStatusTrigger] = useState<TransactionTriggerName | null>(null);
    const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const stateWhenPendingRef = useRef<string | null>(null);
    const { locale } = useLocale();

    // After submit: poll after 5s. If state changed, clear loading. If not, retry once after 5s then clear.
    const POLL_DELAY_MS = 3000;
    useEffect(() => {
        if (!pendingStatusTrigger || !viewProps.pudoTransaction) return;

        stateWhenPendingRef.current = viewProps.pudoTransaction.state;

        const clearPollTimeout = () => {
            if (pollTimeoutRef.current) {
                clearTimeout(pollTimeoutRef.current);
                pollTimeoutRef.current = null;
            }
        };

        const scheduleRetry = () => {
            pollTimeoutRef.current = setTimeout(() => {
                pollTimeoutRef.current = null;
                viewProps.refetch().then(() => {
                    setPendingStatusTrigger(null);
                });
            }, POLL_DELAY_MS);
        };

        pollTimeoutRef.current = setTimeout(() => {
            pollTimeoutRef.current = null;
            viewProps.refetch().then((tx) => {
                const expectedStateChanged = tx && tx.state !== stateWhenPendingRef.current;
                if (expectedStateChanged) {
                    setPendingStatusTrigger(null);
                } else {
                    scheduleRetry();
                }
            });
        }, POLL_DELAY_MS);

        return () => clearPollTimeout();
    }, [pendingStatusTrigger]);

    const handleOpenSlot = async () => {
        if (!viewProps.pudoTransaction?.slot || !navigationState.iot) return;

        const { slot, account_id, spot_id, module_id } = viewProps.pudoTransaction;
        if (!slot?.entity_id || !account_id || !spot_id || !module_id) return;

        setIsOpeningSlot(true);
        await openSlot({
            accountId: account_id,
            spotId: spot_id,
            moduleId: module_id,
            slotId: slot.entity_id,
            iotId: navigationState.iot.id
        });
        setIsOpeningSlot(false);
    };

    const renderContent = () => {
        if (viewProps.loading) {
            return (
                <div className='flex size-full items-center justify-center'>
                    <Spinner />
                </div>
            );
        }

        if (viewProps.error || !viewProps.pudoTransaction) {
            return (
                <div className='flex size-full items-center justify-center'>
                    <div className='text-status-danger'>{viewProps.error}</div>
                </div>
            );
        }

        return (
            <div className='flex size-full flex-col items-start gap-2 overflow-hidden p-6 font-medium'>
                <DetailCard
                    title={
                        <FormattedMessage
                            id='kiosk.manage-spot.pudo-transactions.selected-transaction'
                            defaultMessage='Selected transaction'
                        />
                    }>
                    <DetailCard.Row
                        label={CommonMessage.OBJECTS.COMMON_FIELDS.STATUS}
                        value={
                            <div className='flex items-center gap-2'>
                                {pendingStatusTrigger && <Spinner className='text-border-default fill-brand-primary size-4 shrink-0' />}
                                <TransactionStateBadge state={viewProps.pudoTransaction.state} />
                            </div>
                        }
                    />
                    <DetailCard.Row
                        label={CommonMessage.OBJECTS.TRANSACTION.TRACKING_NUMBER}
                        value={<p>{formatTrackingNumber(viewProps.pudoTransaction.tracking_number ?? '')}</p>}
                    />
                    <DetailCard.Row
                        label={CommonMessage.OBJECTS.SLOT.DEFAULT}
                        value={<p>{viewProps.pudoTransaction.slot?.name || CommonMessage.ERRORS.UNKNOWN_ENTITY(CommonMessage.OBJECTS.SLOT.DEFAULT)}</p>}
                    />
                    <DetailCard.Separator />
                    <DetailCard.Row
                        label={CommonMessage.OBJECTS.COMMON_FIELDS.CREATED_AT}
                        value={<p>{formatDate(new Date(viewProps.pudoTransaction.creation_timestamp), locale)}</p>}
                    />
                    <DetailCard.Row
                        label={CommonMessage.OBJECTS.TRANSACTION.DROP_OFF}
                        value={
                            <p>
                                {viewProps.pudoTransaction.dropped_off_timestamp
                                    ? formatDate(new Date(viewProps.pudoTransaction.dropped_off_timestamp), locale)
                                    : '/'}
                            </p>
                        }
                    />
                    <DetailCard.Row
                        label={CommonMessage.OBJECTS.TRANSACTION.PICK_UP}
                        value={
                            <p>
                                {viewProps.pudoTransaction.picked_up_timestamp
                                    ? formatDate(new Date(viewProps.pudoTransaction.picked_up_timestamp), locale)
                                    : '/'}
                            </p>
                        }
                    />
                </DetailCard>
                <DetailCard
                    title={
                        <FormattedMessage
                            id='kiosk.manage-spot.pudo-transactions.actions'
                            defaultMessage='Actions'
                            description='Title for the actions card on the manage-spot PuDo transaction detail screen'
                        />
                    }>
                    <div className='flex w-full flex-wrap gap-4'>
                        <button
                            type='button'
                            disabled={
                                !!pendingStatusTrigger ||
                                viewProps.pudoTransaction.state === TransactionStateName.TRANSACTION_COMPLETED ||
                                viewProps.pudoTransaction.state === TransactionStateName.TRANSACTION_CANCELLED
                            }
                            className='text-brand-primary font-medium disabled:cursor-not-allowed disabled:opacity-50'
                            onClick={() => setUpdateStatusModalOpen(true)}>
                            {pendingStatusTrigger ? (
                                <FormattedMessage
                                    id='kiosk.manage-spot.pudo-transactions.updating-status'
                                    defaultMessage='Updating status…'
                                />
                            ) : (
                                <FormattedMessage
                                    id='kiosk.manage-spot.slot-detail.update-status'
                                    defaultMessage='Update status'
                                />
                            )}
                        </button>
                        {viewProps.canMoveTransaction && (
                            <button
                                type='button'
                                className='text-brand-primary font-medium'
                                onClick={viewProps.navigateToMove}>
                                {CommonMessage.BUTTONS.MOVE}
                            </button>
                        )}
                    </div>
                </DetailCard>
                <EntityCard
                    title={CommonMessage.OBJECTS.TRANSACTION.SENDERS}
                    entities={viewProps.pudoTransaction.senders}
                    subtitle={
                        <span className='lowercase'>
                            {viewProps.pudoTransaction.senders?.length || 0}{' '}
                            {viewProps.pudoTransaction.senders?.length === 1
                                ? CommonMessage.OBJECTS.TRANSACTION.SENDER
                                : CommonMessage.OBJECTS.TRANSACTION.SENDERS}
                        </span>
                    }
                    icon={ICONS.ENTITY.USER}
                />
                <EntityCard
                    title={CommonMessage.OBJECTS.TRANSACTION.RECEIVERS}
                    entities={viewProps.pudoTransaction.receivers}
                    subtitle={
                        <span className='lowercase'>
                            {viewProps.pudoTransaction.receivers?.length || 0}{' '}
                            {viewProps.pudoTransaction.receivers?.length === 1
                                ? CommonMessage.OBJECTS.TRANSACTION.RECEIVER
                                : CommonMessage.OBJECTS.TRANSACTION.RECEIVERS}
                        </span>
                    }
                    icon={ICONS.ENTITY.USER}
                />
            </div>
        );
    };

    return (
        <div className='bg-background-default flex size-full flex-col items-center overflow-hidden'>
            <UpdateTransactionStatusModal
                transaction={viewProps.pudoTransaction}
                show={updateStatusModalOpen}
                onClose={() => setUpdateStatusModalOpen(false)}
                onSuccess={(trigger) => {
                    if (trigger) setPendingStatusTrigger(trigger);
                    setUpdateStatusModalOpen(false);
                }}
            />
            <KioskNavigationHeader
                title={CommonMessage.DETAIL.MANAGE(CommonMessage.OBJECTS.SPOT.DISTRI_SPOT)}
                onBack={() => navigate(-1)}
            />
            {renderContent()}
            <KioskNavigationFooter
                leftButton={
                    <DefaultSecondaryButton
                        text={CommonMessage.BUTTONS.GO_BACK}
                        onClick={() => navigate(-1)}
                    />
                }
                rightButton={
                    viewProps.pudoTransaction?.slot && navigationState.iot ? (
                        <DefaultPrimaryButton
                            text={
                                <FormattedMessage
                                    id='kiosk.manage-spot.pudo-transactions.open-slot'
                                    defaultMessage='Open SLOT {text}'
                                    values={{ text: viewProps.pudoTransaction.slot.name }}
                                />
                            }
                            onClick={handleOpenSlot}
                            disabled={isOpeningSlot}
                            isLoading={isOpeningSlot}
                        />
                    ) : null
                }
            />
        </div>
    );
};

export default PudoTransactionDetail;
